import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Manage Listings selection using an overrides map on top of API rows.
 *
 * - `rows` stays as immutable API data; user changes are stored in `overrides`.
 * - `effectiveRows` = rows merged with overrides (used for display).
 * - `selection` = enabled IDs derived from effectiveRows.
 * - Overrides persist across searches so the user's intent is never lost.
 * - `netSelectionChange` tells the parent how many listings the user has
 *   net-enabled (+) or net-disabled (−) relative to the server state.
 */
export function useManageListingsSelection({ open, rows, onSaveChanges, refreshListings }) {
    const [overrides, setOverrides] = useState(new Map());
    const [loading, setLoading] = useState(false);

    const prevOpenRef = useRef(false);
    const refreshRef = useRef(refreshListings);
    refreshRef.current = refreshListings;

    const effectiveRows = useMemo(() => {
        if (overrides.size === 0) return rows;
        return rows.map((r) => {
            const override = overrides.get(String(r.id));
            return override ? { ...r, enabled: override.current } : r;
        });
    }, [rows, overrides]);

    const selection = useMemo(
        () => effectiveRows.filter((r) => r.enabled).map((r) => r.id),
        [effectiveRows]
    );

    useEffect(() => {
        const justOpened = open && !prevOpenRef.current;
        const justClosed = !open && prevOpenRef.current;
        prevOpenRef.current = open;

        if (justOpened || justClosed) {
            setOverrides(new Map());
        }

        if (justOpened) {
            refreshRef.current();
        }
    }, [open]);

    const hasChanges = useMemo(() => overrides.size > 0, [overrides]);

    const netSelectionChange = useMemo(() => {
        let net = 0;
        overrides.forEach(({ original, current }) => {
            if (original && !current) net--;
            if (!original && current) net++;
        });
        return net;
    }, [overrides]);

    const toggle = useCallback(
        (id) => {
            setOverrides((prev) => {
                const next = new Map(prev);
                const apiRow = rows.find((r) => r.id === id);
                if (!apiRow) return prev;

                const existing = prev.get(String(id));
                const original = existing ? existing.original : apiRow.enabled;
                const currentEnabled = existing ? existing.current : apiRow.enabled;
                const newEnabled = !currentEnabled;

                if (newEnabled === original) {
                    next.delete(String(id));
                } else {
                    next.set(String(id), { original, current: newEnabled });
                }
                return next;
            });
        },
        [rows]
    );

    const selectAll = useCallback(() => {
        setOverrides((prev) => {
            const next = new Map(prev);
            rows.forEach((r) => {
                const existing = prev.get(String(r.id));
                const original = existing ? existing.original : r.enabled;
                if (original === true) {
                    next.delete(String(r.id));
                } else {
                    next.set(String(r.id), { original, current: true });
                }
            });
            return next;
        });
    }, [rows]);

    const clearSelection = useCallback(() => {
        setOverrides((prev) => {
            const next = new Map(prev);
            rows.forEach((r) => {
                const existing = prev.get(String(r.id));
                const original = existing ? existing.original : r.enabled;
                if (original === false) {
                    next.delete(String(r.id));
                } else {
                    next.set(String(r.id), { original, current: false });
                }
            });
            return next;
        });
    }, [rows]);

    const saveChanges = useCallback(
        async (callback) => {
            if (overrides.size === 0) return;
            setLoading(true);

            const enabled = [];
            const disabled = [];
            overrides.forEach(({ original, current }, id) => {
                if (!original && current) enabled.push(id);
                if (original && !current) disabled.push(id);
            });

            await onSaveChanges({ enabled, disabled });
            await refreshListings();
            setOverrides(new Map());
            setLoading(false);
            callback?.();
        },
        [overrides, onSaveChanges, refreshListings]
    );

    const resetToBaseline = useCallback(() => {
        setOverrides(new Map());
    }, []);

    return {
        selection,
        hasChanges,
        toggle,
        selectAll,
        clearSelection,
        saveChanges,
        resetToBaseline,
        loading,
        effectiveRows,
        netSelectionChange,
    };
}
